import { Box, Button, Flex, FormLabel, Input, Text, useColorModeValue, useDisclosure, useToast } from "@chakra-ui/react";
import FormModal from "../../../components/FormModal";
import { useState } from "react";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { typography } from 'theme/tokens';
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { useUpdateBookingPaymentDeadline } from "../../organization/hooks/useUpdateBookingPaymentDeadline";

// API range for bookingPaymentDeadlineMinutes — kept in sync with
// UpdateOrganizationBookingPaymentDeadlineDto on the API.
//
// 30, not 5: Stripe Checkout refuses an `expires_at` less than 30 minutes
// out, and this value is the default window for EVERY phone/dashboard/
// WhatsApp booking link on the tenant. The API declared @Min(30) all along,
// but the global pipe is soft-mode, so a 5 typed here was accepted, persisted,
// and then failed at Stripe on every subsequent booking. The API now rejects
// it outright; offering it here would only produce a 400.
const MIN_MINUTES = 30;
const MAX_MINUTES = 1430;
const STEP_MINUTES = 5;

export default function BookingPaymentDeadlineCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [minutes, setMinutes] = useState<number>(
        selectedOrganization?.bookingPaymentDeadlineMinutes ?? 30,
    );

    const { mutateAsync: updateDeadline, isLoading } = useUpdateBookingPaymentDeadline(
        selectedOrganization?.id || '',
        () => {
            onClose();
            toast({
                title: translate('organization.booking_payment_deadline_saved'),
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
        },
    );

    const handleSave = async () => {
        try {
            const value = Number.isFinite(minutes) ? minutes : 30;
            await updateDeadline({ bookingPaymentDeadlineMinutes: value });
        } catch (err: any) {
            // 400 from the API carries the validation message ("must be between
            // 30 and 1430 minutes"). Surface it directly so the operator sees why.
            toast({
                title: translate('error'),
                description: err?.message,
                status: 'error',
                duration: 4000,
                position: 'top-right',
            });
        }
    };

    if (!selectedOrganization) return null;

    const currentMinutes = selectedOrganization.bookingPaymentDeadlineMinutes ?? 30;

    return (
        <>
            <PageCard title={translate('organization.booking_payment_deadline')}>
                <Box p={4}>
                    <Box mb={4} color={textColorPrimary}>
                        <Box mb={2}>
                            <strong>{translate('organization.booking_payment_deadline')}: </strong>
                            {currentMinutes} {translate('minutes')}
                        </Box>
                        <Text fontSize={14} color="gray.500">
                            {translate('organization.booking_payment_deadline_helper')}
                        </Text>
                    </Box>
                    <Button
                        mt={3}
                        size="md"
                        onClick={() => {
                            setMinutes(selectedOrganization.bookingPaymentDeadlineMinutes ?? 30);
                            onOpen();
                        }}
                        colorScheme="brand"
                    >
                        {translate('organization.set_booking_payment_deadline')}
                    </Button>
                </Box>
            </PageCard>

            <FormModal
                isOpen={isOpen}
                onClose={onClose}
                title={translate('organization.set_booking_payment_deadline')}
                onSubmit={handleSave}
                submitLabel={translate('save')}
                cancelLabel={translate('cancel')}
                isSubmitting={isLoading}
            >
                <Text fontSize={14}>
                    {translate('organization.booking_payment_deadline_description')}
                </Text>
                <Flex direction="row" justify="center" align="center" py={4}>
                    <Input
                        w={28}
                        type="number"
                        min={MIN_MINUTES}
                        max={MAX_MINUTES}
                        step={STEP_MINUTES}
                        value={Number.isFinite(minutes) ? minutes : ''}
                        onChange={(e) => {
                            const parsed = Number.parseInt(e.target.value, 10);
                            setMinutes(Number.isNaN(parsed) ? 30 : parsed);
                        }}
                    />
                    <FormLabel {...typography.formLabel} mt={2} ml={2}>
                        {translate('minutes')}
                    </FormLabel>
                </Flex>
            </FormModal>
        </>
    );
}
